import React, { Fragment, useState } from "react";
import { LucideIcon, UserIcon } from "lucide-react";
import { cn } from "@utils/utils";
import { useAuth, UserScope } from "@feature/auth";
import { Link, useLocation } from "react-router-dom";
import { ModalLogout } from "@shared/components/ModalLogout";

interface SidebarFooterProps {
  isOpen: boolean;
  isMobile?: boolean;
  onItemClick?: () => void;
}
type IconType =
  | LucideIcon
  | React.ComponentType<{ className?: string; size?: number }>;
interface NavigationItemProps {
  icon: IconType;
  label: string;
  path: string;
  colorActive: string;
  requiredScope?: UserScope | null;
  isOpen: boolean;
  setIsOpenModal: (open: boolean) => void;
  isMobile?: boolean;
  onItemClick?: () => void;
}


const NavigationItem: React.FC<NavigationItemProps> = ({
  icon: Icon,
  label,
  path,
  colorActive,
  isOpen,
  setIsOpenModal,
  isMobile = false,
  onItemClick,
}) => {
  const location = useLocation();
  const isActive = location.pathname === path;

  const handleClick = () => {
    if (path === "") {
      setIsOpenModal(true);
    }
    if (isMobile && onItemClick) {
      onItemClick();
    }
  };

  const content = (
    <>
      <Icon className={cn("h-5 w-5 flex-shrink-0 text-blue-600", isMobile ? "" : "")} />
      {isOpen || isMobile ? (
        <span className="transition-opacity duration-300 truncate min-w-0 font-medium text-slate-700 text-[15px]">{label}</span>
      ) : (
        <div className="absolute left-full ml-2 px-2 py-1 bg-white/90 rounded-md shadow-sm invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-200 whitespace-nowrap">
          {label}
        </div>
      )}
    </>
  );

  return (
    <Fragment>
      {path === "" ? (
        <button
          onClick={handleClick}
          className={cn(
            isMobile
              ? "flex items-center text-foreground gap-3 py-2.5 rounded-2xl transition-colors relative group bg-transparent w-full min-w-0"
              : cn(
                  "flex items-center text-foreground gap-3 py-2 rounded-2xl transition-colors relative group bg-primary-60 w-full min-w-0",
                  isOpen ? "px-4" : "px-2 justify-center",
                  "hover:border-1 hover:border-white",
                  isActive && "border-1 border-white shadow-sm font-medium",
                  isActive &&
                    "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-8 before:bg-primary before:rounded-r-full"
                )
          )}
          style={isActive && !isMobile ? { backgroundColor: colorActive } : {}}
          onMouseEnter={(e) => {
            if (!isActive && !isMobile) {
              e.currentTarget.style.backgroundColor = colorActive;
            }
          }}
          onMouseLeave={(e) => {
            if (!isActive && !isMobile) {
              e.currentTarget.style.backgroundColor = "";
            }
          }}
        >
          {content}
        </button>
      ) : (
        <Link
          to={path}
          className={cn(
            isMobile
              ? "flex items-center text-foreground gap-3 py-2.5 rounded-2xl transition-colors relative group bg-transparent w-full min-w-0"
              : cn(
                  "flex items-center text-foreground gap-3 py-2 rounded-2xl transition-colors relative group bg-primary-60 min-w-0",
                  isOpen ? "px-4" : "px-2 justify-center",
                  "hover:border-1 hover:border-white",
                  isActive && "border-1 border-white shadow-sm font-medium",
                  isActive &&
                    "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-1 before:h-8 before:bg-primary before:rounded-r-full"
                )
          )}
          style={isActive && !isMobile ? { backgroundColor: colorActive } : {}}
          onMouseEnter={(e) => {
            if (!isActive && !isMobile) {
              e.currentTarget.style.backgroundColor = colorActive;
            }
          }}
          onMouseLeave={(e) => {
            if (!isActive && !isMobile) {
              e.currentTarget.style.backgroundColor = "";
            }
          }}
        >
          {content}
        </Link>
      )}
    </Fragment>
  );
};


export const SidebarFooter: React.FC<SidebarFooterProps> = ({ isOpen, isMobile = false, onItemClick }) => {
  const { logout, user } = useAuth();
  const [isOpenModal, setIsOpenModal] = useState(false);

  const navigationItems = [
    {
      icon: UserIcon,
      label: user?.name || "Usuario",
      path: "/profile",
      colorActive: "#a2d0fb",
      requiredScope: "profile" as const,
    },
  ];

  return (
    <div className={cn(isMobile ? "px-6 py-1" : cn("border-t", isOpen ? "p-4" : "p-2"))}>
      <nav className="flex-1">
        <ul className="space-y-2">
          {navigationItems.map((item) => (
            <NavigationItem
              key={item.path}
              icon={item.icon}
              label={item.label}
              path={item.path}
              colorActive={item.colorActive}
              requiredScope={item.requiredScope}
              isOpen={isOpen}
              setIsOpenModal={setIsOpenModal}
              isMobile={isMobile}
              onItemClick={onItemClick}
            />
          ))}
        </ul>
      </nav>

      <ModalLogout
        open={isOpenModal}
        logout={logout}
        user={user}
        onOpenChange={setIsOpenModal}
      />
    </div>
  );
};
